import React, { useEffect, useRef, useState } from "react";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Box, Layers, Minus, Plus, RotateCcw } from "lucide-react";
import { AREAS, DEPOT, HQ } from "@/data/areas";

const CENTER = [8.8017, 53.055];

const STYLE = {
  version: 8,
  sources: {
    osm: {
      type: "raster",
      tiles: [
        "https://a.tile.openstreetmap.org/{z}/{x}/{y}.png",
        "https://b.tile.openstreetmap.org/{z}/{x}/{y}.png",
        "https://c.tile.openstreetmap.org/{z}/{x}/{y}.png",
      ],
      tileSize: 256,
      maxzoom: 19,
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    },
  },
  layers: [
    { id: "bg", type: "background", paint: { "background-color": "#eef6ff" } },
    { id: "osm", type: "raster", source: "osm", paint: { "raster-saturation": -0.28, "raster-contrast": 0.05 } },
  ],
};

const markerEl = (area, onClick) => {
  const el = document.createElement("button");
  el.type = "button";
  el.className = `bs-pin ${area.zone === "kern" ? "bs-pin--kern" : "bs-pin--umland"}`;
  el.setAttribute("aria-label", `${area.n} – ${area.zone === "kern" ? "Bremer Stadtgebiet" : "Umland auf Anfrage"}`);
  el.innerHTML = `<span class="bs-pin__dot"></span><span class="bs-pin__label">${area.n}</span>`;
  el.addEventListener("click", (e) => {
    e.stopPropagation();
    onClick(area);
  });
  return el;
};

const AreaMap3D = ({ focus, onSelect, matches }) => {
  const box = useRef(null);
  const map = useRef(null);
  const markers = useRef({});
  const [is3d, setIs3d] = useState(true);
  const [ready, setReady] = useState(false);
  const onSelectRef = useRef(onSelect);

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    if (map.current || !box.current) return;
    const m = new maplibregl.Map({
      container: box.current,
      style: STYLE,
      center: CENTER,
      zoom: 10.1,
      pitch: 68,
      bearing: -22,
      maxPitch: 80,
      minZoom: 7.5,
      maxZoom: 17,
      attributionControl: { compact: true },
      cooperativeGestures: true,
    });
    map.current = m;
    m.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), "bottom-right");

    m.on("load", () => {
      m.addLayer({
        id: "sky",
        type: "sky",
        paint: { "sky-type": "gradient", "sky-gradient": ["interpolate", ["linear"], ["sky-radial-progress"], 0.75, "#b6d4f5", 1, "#eef6ff"] },
      });

      AREAS.forEach((a) => {
        const mk = new maplibregl.Marker({
          element: markerEl(a, (area) => onSelectRef.current && onSelectRef.current(area)),
        })
          .setLngLat([a.lng, a.lat])
          .addTo(m);
        markers.current[a.n] = mk;
      });

      [
        { p: HQ, cls: "bs-hq" },
        { p: DEPOT, cls: "bs-hq bs-hq--depot" },
      ].forEach(({ p, cls }) => {
        const el = document.createElement("div");
        el.className = cls;
        el.innerHTML = `<span class="bs-hq__ring"></span><span class="bs-hq__label">${p.label}</span>`;
        new maplibregl.Marker({ element: el }).setLngLat([p.lng, p.lat]).addTo(m);
      });

      setReady(true);
    });

    return () => {
      m.remove();
      map.current = null;
      markers.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Suche: Treffer hervorheben
  useEffect(() => {
    if (!ready) return;
    const names = matches ? matches.map((a) => a.n) : null;
    Object.entries(markers.current).forEach(([n, mk]) => {
      const el = mk.getElement();
      el.classList.toggle("is-dim", Boolean(names) && !names.includes(n));
      el.classList.toggle("is-match", Boolean(names) && names.includes(n));
    });
  }, [matches, ready]);

  // Fokus auf ausgewählten Ort
  useEffect(() => {
    if (!ready || !focus || !map.current) return;
    Object.entries(markers.current).forEach(([n, mk]) =>
      mk.getElement().classList.toggle("is-active", n === focus.n),
    );
    map.current.flyTo({
      center: [focus.lng, focus.lat],
      zoom: 12.4,
      pitch: is3d ? 60 : 0,
      duration: 1600,
      essential: true,
    });
  }, [focus, ready, is3d]);

  const reset = () => {
    if (!map.current) return;
    Object.values(markers.current).forEach((mk) => {
      mk.getElement().classList.remove("is-active", "is-dim", "is-match");
    });
    map.current.flyTo({ center: CENTER, zoom: 10.1, pitch: is3d ? 68 : 0, bearing: -22, duration: 1400 });
  };

  const toggle3d = () => {
    const next = !is3d;
    setIs3d(next);
    map.current?.easeTo({ pitch: next ? 68 : 0, bearing: next ? -22 : 0, duration: 1100 });
  };

  return (
    <div className="relative overflow-hidden rounded-[22px] border border-[#DCE5EF] bg-[#EEF6FF]">
      <div
        ref={box}
        data-testid="area-map-3d"
        className="h-[340px] w-full sm:h-[420px] lg:h-[520px]"
      />

      {/* Steuerung */}
      <div className="absolute left-3 top-3 flex flex-col gap-2">
        <button
          type="button"
          onClick={toggle3d}
          data-testid="map-toggle-3d"
          aria-pressed={is3d}
          className="flex items-center gap-2 rounded-full bg-white/95 px-3.5 py-2 text-[12px] font-semibold text-[#062B52] shadow-[0_10px_26px_-14px_rgba(6,43,82,0.7)] backdrop-blur transition-colors hover:text-[#0757B8]"
        >
          {is3d ? <Box className="h-3.5 w-3.5 text-[#FF6B00]" /> : <Layers className="h-3.5 w-3.5 text-[#0757B8]" />}
          {is3d ? "3D-Ansicht" : "2D-Ansicht"}
        </button>
        <button
          type="button"
          onClick={reset}
          data-testid="map-reset"
          className="flex items-center gap-2 rounded-full bg-white/95 px-3.5 py-2 text-[12px] font-semibold text-[#062B52] shadow-[0_10px_26px_-14px_rgba(6,43,82,0.7)] backdrop-blur transition-colors hover:text-[#0757B8]"
        >
          <RotateCcw className="h-3.5 w-3.5 text-[#0757B8]" /> Gesamtgebiet
        </button>
      </div>

      <div className="absolute right-3 top-3 flex gap-2">
        <button
          type="button"
          aria-label="Hineinzoomen"
          data-testid="map-zoom-in"
          onClick={() => map.current?.zoomIn({ duration: 400 })}
          className="flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-[#062B52] shadow-[0_10px_26px_-14px_rgba(6,43,82,0.7)] backdrop-blur hover:text-[#0757B8]"
        >
          <Plus className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label="Herauszoomen"
          data-testid="map-zoom-out"
          onClick={() => map.current?.zoomOut({ duration: 400 })}
          className="flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-[#062B52] shadow-[0_10px_26px_-14px_rgba(6,43,82,0.7)] backdrop-blur hover:text-[#0757B8]"
        >
          <Minus className="h-4 w-4" />
        </button>
      </div>

      {/* Legende */}
      <div className="pointer-events-none absolute bottom-3 left-3 flex flex-col gap-1.5 rounded-xl bg-white/92 px-3.5 py-2.5 text-[11px] backdrop-blur">
        <span className="flex items-center gap-2 font-medium text-[#10253A]">
          <span className="h-2.5 w-2.5 rounded-full bg-[#0757B8]" /> Bremer Stadtgebiet
        </span>
        <span className="flex items-center gap-2 text-[#66788A]">
          <span className="h-2.5 w-2.5 rounded-full border-2 border-[#FF6B00] bg-white" /> Umland
          auf Anfrage
        </span>
      </div>
    </div>
  );
};

export default AreaMap3D;
